import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Send, CheckCircle, XCircle, Award, BookOpen, Mail, Pencil } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => (v ? new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtDT = (v) => (v ? new Date(v).toLocaleString() : "—");
const KIND_LABEL = { certificate: "Certificate", assessment_result: "Assessment result", admin_broadcast: "Admin email", password_reset: "Password reset", curbside_answer: "Curbside answer", assessment_admin: "Admin notice", survey_admin: "Admin notice" };

function Section({ icon: Icon, title, children, testId }) {
  return (
    <section data-testid={testId}>
      <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500 mb-2">
        <Icon className="w-4 h-4" /> {title}
      </h3>
      {children}
    </section>
  );
}

export function StudentDetailDialog({ studentId, onClose, onEdit }) {
  const [data, setData] = useState(null);
  const [busyCert, setBusyCert] = useState(null);
  const token = localStorage.getItem("session_token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [reloadKey, setReloadKey] = useState(0);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const load = () => setReloadKey((k) => k + 1);

  useEffect(() => { setData(null); }, [studentId]);

  useEffect(() => {
    if (!studentId) return undefined;
    let cancelled = false;
    axios.get(`${API}/admin/students/${studentId}/detail`, { headers })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) { toast.error("Failed to load student"); onCloseRef.current(); } });
    return () => { cancelled = true; };
  }, [studentId, reloadKey, headers]);

  const downloadCert = async (cert) => {
    try {
      const res = await axios.get(`${API}/admin/certificates/${studentId}/${cert.course_id}/download`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Certificate_${cert.course_title}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Failed to download certificate");
    }
  };

  const resendCert = async (cert) => {
    setBusyCert(cert.id);
    try {
      const res = await axios.post(`${API}/admin/certificates/${cert.id}/resend`, {}, { headers });
      toast.success(res.data.message);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend certificate");
    } finally {
      setBusyCert(null);
    }
  };

  const s = data?.student;
  return (
    <Dialog open={Boolean(studentId)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="student-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 pr-6">
            <span data-testid="student-detail-name">{s ? s.name || s.email : "Loading..."}</span>
            {s && (
              <Button variant="outline" size="sm" onClick={() => onEdit(s)} className="gap-1" data-testid="student-detail-edit">
                <Pencil className="w-3 h-3" /> Edit
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>
        {!data ? (
          <p className="text-sm text-slate-500 py-8 text-center">Loading...</p>
        ) : (
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm bg-slate-50 rounded-lg p-4" data-testid="student-detail-profile">
              <p><span className="text-slate-500">Email:</span> <a href={`mailto:${s.email}`} className="text-blue-700">{s.email}</a></p>
              <p><span className="text-slate-500">Account:</span> {s.account_type === "lead" ? "Quick-register lead" : "Full account"}</p>
              <p><span className="text-slate-500">State / License:</span> {[s.state, s.license_number].filter(Boolean).join(" · ") || "—"}</p>
              <p><span className="text-slate-500">AGD #:</span> {s.agd_number || "—"}</p>
              <p><span className="text-slate-500">Joined:</span> {fmt(s.created_at)}</p>
              <p><span className="text-slate-500">Subscription:</span> {data.subscriptions.filter((x) => x.status === "active").map((x) => x.plan_id).join(", ") || "None"}</p>
            </div>

            <Section icon={BookOpen} title={`Courses (${data.courses.length})`} testId="student-detail-courses">
              {data.courses.length === 0 ? <p className="text-sm text-slate-500">No course activity yet.</p> : (
                <div className="divide-y border rounded-lg">
                  {data.courses.map((c) => (
                    <div key={c.course_id} className="p-3 text-sm flex flex-col sm:flex-row sm:items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate" style={{ color: "#092036" }}>{c.title}</p>
                        <p className="text-xs text-slate-500">
                          Started {fmt(c.started_at)} · {c.lessons_completed} lesson{c.lessons_completed === 1 ? "" : "s"} · {c.time_spent_minutes} min
                          {c.attempts.length > 0 && ` · ${c.attempts.length} attempt${c.attempts.length === 1 ? "" : "s"}, best ${c.best_score}%`}
                        </p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${c.status === "completed" ? "bg-green-100 text-green-800" : c.status === "passed_assessment" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-700"}`}>
                        {c.status === "completed" ? "Completed" : c.status === "passed_assessment" ? "Passed — survey pending" : "In progress"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section icon={Award} title={`Certificates (${data.certificates.length})`} testId="student-detail-certificates">
              {data.certificates.length === 0 ? <p className="text-sm text-slate-500">No certificates yet.</p> : (
                <div className="divide-y border rounded-lg">
                  {data.certificates.map((c) => (
                    <div key={c.id} className="p-3 text-sm flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`cert-row-${c.id}`}>
                      <div className="flex-1">
                        <p className="font-medium" style={{ color: "#092036" }}>{c.course_title}</p>
                        <p className="text-xs text-slate-500">Issued {fmt(c.issued_at)} · Code {c.id.slice(0, 12).toUpperCase()}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => downloadCert(c)} className="gap-1" data-testid={`cert-download-${c.id}`}>
                          <Download className="w-3 h-3" /> PDF
                        </Button>
                        <Button size="sm" onClick={() => resendCert(c)} disabled={busyCert === c.id} className="gap-1 bg-[#102862] hover:bg-[#1a3a7a] text-white" data-testid={`cert-resend-${c.id}`}>
                          <Send className="w-3 h-3" /> {busyCert === c.id ? "Sending..." : "Resend email"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section icon={Mail} title={`Emails received (${data.emails.length})`} testId="student-detail-emails">
              {data.emails.length === 0 ? <p className="text-sm text-slate-500">No emails logged yet.</p> : (
                <div className="divide-y border rounded-lg max-h-64 overflow-y-auto">
                  {data.emails.map((e) => (
                    <div key={e.id} className="p-2.5 text-sm flex items-center gap-3">
                      {e.status === "sent" ? <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" title={e.error || ""} />}
                      <div className="flex-1 min-w-0">
                        <p className="truncate" style={{ color: "#092036" }}>{e.subject}</p>
                        <p className="text-xs text-slate-500">{KIND_LABEL[e.kind] || e.kind} · {fmtDT(e.sent_at)}{e.status !== "sent" && e.error ? ` · ${e.error}` : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
